import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { SmartDisplay as SmartDisplayIcon } from '@mui/icons-material';

type TrainingLevel = 'beginner' | 'intermediate' | 'advanced' | 'pro';
type ContentType =
  | 'self-practice'
  | 'joy-fun-learning'
  | 'coaching'
  | 'strength-conditioning'
  | 'mental-health'
  | 'self-responsibility'
  | 'rules-fitness';

interface TrainingContentItem {
  id: string;
  trainingLevel: TrainingLevel;
  title: string;
  description?: string;
  tips?: string;
  referenceVideos?: string[];
}

interface TrainingContentPanelProps {
  selectedSport: string;
  trainingLevel?: TrainingLevel;
  contentType: ContentType;
  title: string;
}

const TrainingContentPanel: React.FC<TrainingContentPanelProps> = ({
  selectedSport,
  trainingLevel = 'beginner',
  contentType,
  title,
}) => {
  const [selectedLevel, setSelectedLevel] = useState<TrainingLevel>(trainingLevel);
  const [items, setItems] = useState<TrainingContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        sport: selectedSport || 'Badminton',
        trainingLevel: selectedLevel,
        contentType,
      });
      const response = await fetch(`/api/training-content-items?${params.toString()}`);
      const result = await response.json();
      if (!result.success) {
        setError(result.message || 'Failed to load content');
        setItems([]);
        return;
      }
      setItems(result.data || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadItems();
  }, [selectedSport, selectedLevel, contentType]);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        {title}
      </Typography>
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Training Level</InputLabel>
          <Select
            value={selectedLevel}
            label="Training Level"
            onChange={(e) => setSelectedLevel(e.target.value as TrainingLevel)}
          >
            <MenuItem value="beginner">Beginner</MenuItem>
            <MenuItem value="intermediate">Intermediate</MenuItem>
            <MenuItem value="advanced">Advanced</MenuItem>
            <MenuItem value="pro">Pro</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={1.5}>
        {loading ? (
          <Typography variant="body2">Loading...</Typography>
        ) : items.length === 0 ? (
          <Alert severity="info">No items available for this level yet.</Alert>
        ) : (
          items.map((item) => (
            <Card key={item.id}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                    {item.title}
                  </Typography>
                  <Chip size="small" label={item.trainingLevel} />
                </Stack>
                {item.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    {item.description}
                  </Typography>
                )}
                {item.tips && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    {item.tips}
                  </Typography>
                )}
                {Array.isArray(item.referenceVideos) && item.referenceVideos.length > 0 && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<SmartDisplayIcon sx={{ fontSize: 14 }} />}
                    onClick={() => window.open(item.referenceVideos?.[0], '_blank', 'noopener,noreferrer')}
                    sx={{ textTransform: 'none', px: 0 }}
                  >
                    Watch
                  </Button>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </Stack>
    </Box>
  );
};

export default TrainingContentPanel;
